/**
 * Support Request Types & Constants
 * For the Intranet "Raise Hand" Support System
 */

export type SupportStatus = 'Pending' | 'In Progress' | 'Resolved' | 'Closed';

export interface SupportCategory {
  id: string;
  name: string;
  description: string;
  suggestedAction?: string;
}

export const SUPPORT_CATEGORIES: SupportCategory[] = [
  {
    id: 'permissions',
    name: 'Permissions & Access',
    description: 'Need view/edit access, access denied, locked cells/tab, or sharing permissions.',
    suggestedAction: 'Access grant or permission update',
  },
  {
    id: 'functionality',
    name: 'Sheet Functionality & Formulas',
    description: 'Broken formulas, calculation errors, filter/sort not working, or script issues.',
    suggestedAction: 'Formula repair or script fix',
  },
  {
    id: 'sheet_changes',
    name: 'Sheet Changes & Layout Updates',
    description: 'Request new columns/fields, update dropdown options, change layout or styling.',
    suggestedAction: 'Sheet design or column modification',
  },
  {
    id: 'it_support',
    name: 'IT & Technical Support',
    description: 'Browser issues, document not loading, device problems, login or network errors.',
    suggestedAction: 'IT helpdesk troubleshooting',
  },
  {
    id: 'supply_orders',
    name: 'Supply & Inventory Orders',
    description: 'Need to order clinic supplies, office inventory, medical products, or equipment.',
    suggestedAction: 'Supply order processing',
  },
  {
    id: 'doc_update',
    name: 'Printable Form / Doc Update',
    description: 'Outdated printable PDF/form, correction needed, revision, or version update.',
    suggestedAction: 'Document revision or PDF upload',
  },
  {
    id: 'new_sheet',
    name: 'New Sheet / Template Request',
    description: 'Request creation of a brand new spreadsheet, workflow tracker, or template.',
    suggestedAction: 'New sheet creation',
  },
  {
    id: 'general_help',
    name: 'General Help & Inquiries',
    description: 'How-to questions, clinic workflow guidance, general feedback, or other help.',
    suggestedAction: 'General support inquiry',
  },
];

export interface SupportAssignee {
  id: string;
  name: string;
  email: string;
  roleDescription?: string;
}

export const SUPPORT_ASSIGNEES: SupportAssignee[] = [
  {
    id: 'adminhelp',
    name: 'Admin & Support Team',
    email: 'adminhelp@ssdspc.com',
    roleDescription: 'General Clinic Triage & Support Team',
  },
  {
    id: 'agonzalez',
    name: 'Alvaro Gonzalez',
    email: 'agonzalez@ssdspc.com',
    roleDescription: 'Systems & Intranet Administrator',
  },
  {
    id: 'aadwell',
    name: 'Adam Adwell',
    email: 'aadwell@ssdspc.com',
    roleDescription: 'Clinical & Operations Lead',
  },
  {
    id: 'tbunton',
    name: 'Taegan Bunton',
    email: 'tbunton@ssdspc.com',
    roleDescription: 'Billing & Administration',
  },
  {
    id: 'lsanford',
    name: 'Leslie Sanford',
    email: 'lsanford@ssdspc.com',
    roleDescription: 'Clinical Lead & Surgery Coordinator',
  },
];

export interface SupportTargetFile {
  fileId: string;
  fileName: string;
  fileUrl: string;
  categoryOrTeam?: string;
  sourceColumn: 1 | 2 | 3; // 1: Daily Tasks, 2: Printable Docs, 3: All Sheets
  columnLabel: string;
}

export interface SupportRequest {
  id: string; // e.g. "req-1724859281234-a1b2"
  requestNumber?: number; // Sequential #1, #2...
  userName: string;
  userEmail: string;
  fileId: string;
  fileName: string;
  fileUrl: string;
  fileCategory: string;
  sourceColumn: 1 | 2 | 3;
  sourceColumnLabel: string;
  issueCategory: string; // e.g. "Permissions & Access", "Sheet Functionality & Formulas", etc.
  assignedTo?: string; // Backwards compatible fallback
  assignedEmail?: string;
  description: string;
  status: SupportStatus;
  statusNotes?: string;
  resolvedAt?: string;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
}
